import React, { useState, useEffect } from "react";
import { Analytics } from "@vercel/analytics/react";
import { DashboardHeader } from "./components/DashboardHeader";
import { NavigationSidebar } from "./components/NavigationSidebar";
import { MainDashboard } from "./components/MainDashboard";
import { AlertsPanel } from "./components/AlertsPanel";
import { AIQualityControl } from "./components/AIQualityControl";
import ReportDetail from "./components/ReportDetail";
import { SettingsPage } from "./components/SettingsPage";
import { HelpPage } from "./components/HelpPage";
import { AccountSettingsPage } from "./components/AccountSettingsPage";
import { AdminDashboard } from "./components/AdminDashboard";
import { UserSettingsPage } from "./components/UserSettingsPage";
import { ExportModal } from "./components/ExportModal";
import { ScheduleReviewModal } from "./components/ScheduleReviewModal";
import { UserDropdown } from "./components/UserDropdown";
import { apiClient } from "./lib/api";
import PDFViewerWithHighlights from './components/PDFViewerWithHighlights_SIMPLE';
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { ArchivePage } from "./components/ArchivePage";
import PersonalizedFeed from "./components/PersonalizedFeed";
import { CMSMarketAnalysis } from "./components/CMSMarketAnalysis";

type ViewMode = 'dashboard' | 'report-detail';
type ApiStatus = 'loading' | 'connected' | 'error';

interface Update {
  id: string;
  title: string;
  [key: string]: any;
}

// Main App Component (wrapped with auth)
function AppContent() {
  console.log("✅ App.tsx is running - AUTH INTEGRATED");

  const { logout } = useAuth();
  const [activeView, setActiveView] = useState("dashboard");
  const [apiStatus, setApiStatus] = useState<ApiStatus>("loading");
  const [selectedUpdate, setSelectedUpdate] = useState<Update | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('dashboard');
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [selectedDocId, setSelectedDocId] = useState<number | null>(null);
  const [archiveCount, setArchiveCount] = useState<number | null>(null);

  // Modal and dropdown states
  const [showAlerts, setShowAlerts] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');

  // Backend connection check on mount
  useEffect(() => {
    checkBackendConnection();
    loadArchiveCount();
  }, []);

  const checkBackendConnection = async () => {
    try {
      console.log("🔗 Testing backend connection...");
      setApiStatus("loading");

      // Just check if the backend is reachable - don't call non-existent endpoints
      await apiClient.healthCheck();

      setApiStatus("connected");
      console.log("✅ Backend connected successfully!");
    } catch (error) {
      setApiStatus("error");
      console.error("❌ Backend connection failed:", error);
    }
  };

  const loadArchiveCount = async () => {
    try {
      const health = await apiClient.getArchiveHealth();
      if (health?.total_documents !== undefined) {
        setArchiveCount(health.total_documents);
      }
    } catch (error) {
      console.warn('Archive count unavailable', error);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
    setApiStatus("loading");
    setActiveView('dashboard');
    setViewMode('dashboard');
  };

  // Handle report selection
  const handleReportClick = (update: Update) => {
    console.log('📊 Report clicked:', update.title);
    setSelectedUpdate(update);
    setViewMode('report-detail');
  };

  // Handle back to dashboard
  const handleBackToDashboard = () => {
    console.log('◀️ Back to dashboard');
    setViewMode('dashboard');
    setSelectedUpdate(null);
    setActiveView('dashboard');
  };

  // Handle header actions
  const handleNotificationClick = () => {
    setShowAlerts(!showAlerts);
  };

  const handleHelpClick = () => {
    setActiveView('help');
    setViewMode('dashboard');
  };

  const handleSettingsClick = () => {
    setActiveView('settings');
    setViewMode('dashboard');
  };

  const handleProfileClick = () => {
    setShowUserDropdown(!showUserDropdown);
  };

  const handleExportClick = () => {
    setShowExportModal(true);
  };

  const handleScheduleClick = () => {
    setShowScheduleModal(true);
  };

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    console.log('🔍 Search query:', query);
  };

  // Handle user dropdown navigation
  const handleUserDropdownNavigate = (page: string) => {
    switch (page) {
      case 'account':
        setActiveView('account-settings');
        break;
      case 'settings':
        setActiveView('settings');
        break;
      case 'help':
        setActiveView('help');
        break;
      case 'billing':
      case 'notifications':
      case 'security':
        setActiveView('settings');
        break;
      default:
        break;
    }
    setViewMode('dashboard');
    setShowUserDropdown(false);
  };

  // If showing PDF viewer, render it full screen
  if (showPdfViewer && selectedDocId !== null) {
    return (
      <PDFViewerWithHighlights 
        documentId={selectedDocId}
        onClose={() => {
          console.log('✖️ Closing PDF viewer');
          setShowPdfViewer(false);
          setSelectedDocId(null);
        }}
      />
    );
  }

  // If in report detail mode, show report detail
  if (viewMode === 'report-detail' && selectedUpdate) {
    return (
      <div className="h-screen flex flex-col bg-background">
        <DashboardHeader 
          onNotificationClick={handleNotificationClick}
          onHelpClick={handleHelpClick}
          onSettingsClick={handleSettingsClick}
          onProfileClick={handleProfileClick}
          onExportClick={handleExportClick}
          onScheduleClick={handleScheduleClick}
          onSearchChange={handleSearchChange}
        />
        <ReportDetail
          document={selectedUpdate}
          onBack={handleBackToDashboard}
        />
      </div>
    );
  }

  // Render the appropriate page based on activeView
  const renderContent = () => {
    switch (activeView) {
      case 'ai-quality-control':
        return <AIQualityControl />;
      case 'admin-dashboard':
        return <AdminDashboard />;
      case 'cms-market-analysis':
        return <CMSMarketAnalysis />;
      case 'user-settings':
        return <UserSettingsPage />;
      case 'settings':
        return <SettingsPage />;
      case 'help':
        return <HelpPage />;
      case 'account-settings':
        return <AccountSettingsPage />;
      case 'archive':
        return <ArchivePage />;
      case 'dashboard':
      default:
        return <MainDashboard onReportClick={handleReportClick} searchQuery={searchQuery} />;
    }
  };

  // Default dashboard view with backend connection indicator
  return (
    <>
      <Analytics />
      <div className="h-screen flex flex-col bg-background relative">
      {/* Backend Connection Status Banner */}
      {apiStatus !== "connected" && (
        <div className={`px-4 py-2 text-sm text-center ${
          apiStatus === "loading" 
            ? "bg-blue-100 text-blue-800" 
            : "bg-red-100 text-red-800"
        }`}>
          {apiStatus === "loading" ? "🔗 Connecting to backend..." : "❌ Backend connection failed"}
        </div>
      )}
      
      <DashboardHeader 
        onNotificationClick={handleNotificationClick}
        onHelpClick={handleHelpClick}
        onSettingsClick={handleSettingsClick}
        onProfileClick={handleProfileClick}
        onExportClick={handleExportClick}
        onScheduleClick={handleScheduleClick}
        onSearchChange={handleSearchChange}
      />
      <div className="flex-1 flex overflow-hidden">
        <NavigationSidebar activeView={activeView} onViewChange={setActiveView} archiveCount={archiveCount} />
        <div className="flex-1 overflow-auto">
          {renderContent()}
        </div>
        
        {/* Alerts panel - toggles and overlays */}
        {showAlerts && activeView === "dashboard" && (
          <>
            {/* Overlay backdrop */}
            <div 
              className="absolute inset-0 bg-black/20 z-10"
              onClick={() => setShowAlerts(false)}
            />
            
            {/* Alerts Panel - slides in from right */}
            <div className="absolute right-0 top-0 bottom-0 w-96 z-20">
              <AlertsPanel onClose={() => setShowAlerts(false)} />
            </div>
          </>
        )}
      </div>

      {/* User Dropdown */}
      {showUserDropdown && (
        <UserDropdown
          isOpen={showUserDropdown}
          onClose={() => setShowUserDropdown(false)}
          onLogout={handleLogout}
          onNavigate={handleUserDropdownNavigate}
        />
      )}

      {/* Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
      />

      {/* Schedule Review Modal */}
      <ScheduleReviewModal
        isOpen={showScheduleModal}
        onClose={() => setShowScheduleModal(false)}
      />
      </div>
    </>
  );
}

// Export App wrapped with AuthProvider and ProtectedRoute
export default function App() {
  return (
    <AuthProvider>
      <ProtectedRoute>
        <AppContent />
      </ProtectedRoute>
    </AuthProvider>
  );
}
